import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api, { setAuthBootstrapping } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function applySession(token, loggedInUser, setUser, setSessionChecked, setImpersonation, impersonation = null) {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(loggedInUser));
  setUser(loggedInUser);
  setImpersonation(impersonation);
  setSessionChecked(true);
  setAuthBootstrapping(false);
}

/** Fire-and-forget calendar pull once per browser session (login already syncs server-side). */
function kickSilentCalendarSync() {
  // Must never throw: callers run this inside the /auth/me chain, where a
  // throw would hit the shared catch and clear a valid session.
  try {
    if (sessionStorage.getItem('gcal-autosync') === '1') return;
    sessionStorage.setItem('gcal-autosync', '1');
    api.post('/google-calendar/sync')?.catch(() => {
      /* not connected / no permission — ignore */
    });
  } catch {
    /* private mode or unavailable transport — sync is best effort */
  }
}

export const AuthProvider = ({ children, initialUser = null }) => {
  const [user, setUser] = useState(initialUser);
  /** False until token/no-token bootstrap finishes — gates PrivateRoute. */
  const [sessionChecked, setSessionChecked] = useState(Boolean(initialUser));
  const [impersonation, setImpersonation] = useState(null);
  const bootstrapAbortRef = useRef(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setImpersonation(null);
  }, []);

  useEffect(() => {
    if (initialUser) {
      setSessionChecked(true);
      return undefined;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setSessionChecked(true);
      return undefined;
    }

    const controller = new AbortController();
    bootstrapAbortRef.current = controller;
    setAuthBootstrapping(true);
    setSessionChecked(false);
    setUser(null);
    setImpersonation(null);

    api
      .get('/auth/me', { signal: controller.signal })
      .then((response) => {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setImpersonation(response.data.impersonation?.active ? response.data.impersonation : null);
        kickSilentCalendarSync();
      })
      .catch((error) => {
        if (error.code === 'ERR_CANCELED') return;
        clearSession();
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAuthBootstrapping(false);
          setSessionChecked(true);
        }
      });

    return () => {
      controller.abort();
      setAuthBootstrapping(false);
    };
  }, [initialUser, clearSession]);

  const loginWithGoogle = async (credential) => {
    const response = await api.post('/auth/google', { credential });
    const { token, user: loggedInUser } = response.data;
    applySession(token, loggedInUser, setUser, setSessionChecked, setImpersonation, null);
    kickSilentCalendarSync();
    return loggedInUser;
  };

  /** Complete redirect-based Google OAuth (login + calendar consent). */
  const applyGoogleSession = useCallback(async (token) => {
    localStorage.setItem('token', token);
    setAuthBootstrapping(true);
    try {
      const me = await api.get('/auth/me');
      applySession(
        token,
        me.data.user,
        setUser,
        setSessionChecked,
        setImpersonation,
        me.data.impersonation?.active ? me.data.impersonation : null
      );
      kickSilentCalendarSync();
      return me.data.user;
    } catch (e) {
      clearSession();
      throw e;
    } finally {
      setAuthBootstrapping(false);
    }
  }, [clearSession]);

  const impersonate = async (userId) => {
    const response = await api.post('/auth/impersonate', { userId });
    const { token, user: targetUser } = response.data;
    // Fetch /me so we get impersonator details for the banner
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(targetUser));
    setUser(targetUser);
    try {
      const me = await api.get('/auth/me');
      setUser(me.data.user);
      localStorage.setItem('user', JSON.stringify(me.data.user));
      setImpersonation(me.data.impersonation?.active ? me.data.impersonation : { active: true });
    } catch {
      setImpersonation({ active: true });
    }
    setSessionChecked(true);
    setAuthBootstrapping(false);
    return targetUser;
  };

  const stopImpersonating = async () => {
    const response = await api.post('/auth/stop-impersonation');
    const { token, user: adminUser } = response.data;
    applySession(token, adminUser, setUser, setSessionChecked, setImpersonation, null);
    return adminUser;
  };

  const logout = () => {
    bootstrapAbortRef.current?.abort();
    setAuthBootstrapping(false);
    try {
      sessionStorage.removeItem('gcal-autosync');
    } catch {
      /* ignore */
    }
    clearSession();
    setSessionChecked(true);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setImpersonation(response.data.impersonation?.active ? response.data.impersonation : null);
      return updatedUser;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearSession();
      }
      throw error;
    }
  };

  const loading = !sessionChecked;

  return (
    <AuthContext.Provider
      value={{
        user,
        loginWithGoogle,
        applyGoogleSession,
        logout,
        loading,
        sessionChecked,
        refreshUser,
        impersonate,
        stopImpersonating,
        impersonation,
        isImpersonating: Boolean(impersonation?.active),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
